import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  FileText,
  PackageCheck,
  Truck,
} from 'lucide-react';
import type {
  Order,
  OrderItem,
  OrderStatus,
  OrderStatusHistoryEntry,
  TrackingCarrier,
} from '@rosita/shared';
import { ORDER_STATUSES, TRACKING_CARRIERS } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { api, apiBaseUrl, extractError } from '../../lib/api';
import { formatDate, formatDateTime, formatEur } from '../../lib/format';
import { OrderStatusBadge } from '../../components/admin/OrderStatusBadge';
import { SectionCard } from '../../components/admin/SectionCard';
import { ConfirmDialog } from '../../components/admin/ConfirmDialog';
import { ShipOrderDialog } from '../../components/admin/ShipOrderDialog';
import { useToast } from '../../components/admin/Toast';

interface OrderDetail extends Order {
  items: OrderItem[];
  history: OrderStatusHistoryEntry[];
  customerEmail: string | null;
}

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const secondaryBtn =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm hover:bg-admin-surface-alt disabled:opacity-50';
const actionBtn =
  'inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50';
const thClass =
  'p-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-muted';
const thRight = `${thClass} text-right`;

const cttTrackingUrl = (trackingNumber: string): string =>
  `https://appserver2.ctt.pt/CustomerArea/PublicArea_Detail?Reference=${encodeURIComponent(trackingNumber)}`;

export default function AdminOrderDetailPage(): JSX.Element {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const toast = useToast();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  // Guided actions
  const [pendingAction, setPendingAction] = useState<'preparing' | 'delivered' | null>(null);
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  // Manual override
  const [showManual, setShowManual] = useState(false);
  const [manualStatus, setManualStatus] = useState<OrderStatus>('preparing');
  const [manualNote, setManualNote] = useState('');
  const [manualConfirm, setManualConfirm] = useState(false);
  const [trackingCarrier, setTrackingCarrier] = useState<TrackingCarrier>('ctt');
  const [trackingNumber, setTrackingNumber] = useState('');
  // Internal notes
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const load = async (): Promise<void> => {
    try {
      const response = await api.get<OrderDetail>(`/admin/orders/${orderNumber}`);
      setOrder(response.data);
      setManualStatus(response.data.status);
      if (response.data.trackingCarrier) {
        setTrackingCarrier(response.data.trackingCarrier as TrackingCarrier);
      }
      setTrackingNumber(response.data.trackingNumber ?? '');
      setNotesDraft(response.data.internalNotes ?? '');
    } catch {
      setNotFound(true);
    }
  };

  useEffect(() => {
    setOrder(null);
    setNotFound(false);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  const patchStatus = async (payload: {
    status: OrderStatus;
    note?: string;
    trackingCarrier?: TrackingCarrier;
    trackingNumber?: string;
  }): Promise<boolean> => {
    try {
      await api.patch(`/admin/orders/${orderNumber}/status`, payload);
      toast.success('Estado atualizado.');
      await load();
      return true;
    } catch (err) {
      toast.error(extractError(err, 'Falha ao atualizar o estado'));
      return false;
    }
  };

  const confirmGuidedAction = async (): Promise<void> => {
    if (!pendingAction) return;
    await patchStatus({ status: pendingAction });
    setPendingAction(null);
  };

  const shipOrder = async (carrier: TrackingCarrier, tracking: string): Promise<void> => {
    const ok = await patchStatus({
      status: 'shipped',
      trackingCarrier: carrier,
      trackingNumber: tracking,
    });
    if (ok) setShipDialogOpen(false);
  };

  const submitManual = async (): Promise<void> => {
    const payload: Parameters<typeof patchStatus>[0] = {
      status: manualStatus,
      note: manualNote || undefined,
    };
    if (manualStatus === 'shipped') {
      payload.trackingCarrier = trackingCarrier;
      payload.trackingNumber = trackingNumber.trim();
    }
    const ok = await patchStatus(payload);
    if (ok) {
      setManualNote('');
      setManualConfirm(false);
      setShowManual(false);
    } else {
      setManualConfirm(false);
    }
  };

  const saveNotes = async (): Promise<void> => {
    setSavingNotes(true);
    try {
      await api.patch(`/admin/orders/${orderNumber}/notes`, { internalNotes: notesDraft });
      toast.success('Notas internas guardadas.');
      setOrder((prev) => (prev ? { ...prev, internalNotes: notesDraft || null } : prev));
    } catch (err) {
      toast.error(extractError(err, 'Falha ao guardar as notas'));
    } finally {
      setSavingNotes(false);
    }
  };

  if (notFound) {
    return (
      <>
        <SEO title="Encomenda não encontrada" noindex />
        <Link
          to="/admin/encomendas"
          className="inline-flex items-center gap-1.5 text-sm text-admin-text-soft hover:text-admin-primary"
        >
          <ArrowLeft aria-hidden className="h-4 w-4" /> Encomendas
        </Link>
        <div className="mt-8 rounded-admin-lg border border-admin-border bg-admin-surface p-8 text-center">
          <p className="text-lg font-medium">Encomenda não encontrada</p>
          <p className="mt-1 text-sm text-admin-text-soft">
            Não existe nenhuma encomenda com o número {orderNumber}.
          </p>
        </div>
      </>
    );
  }

  if (!order) return <p className="text-admin-text-soft">{t.common.loading}</p>;

  const apiBase = apiBaseUrl;
  const isPickup = Boolean(order.pickupPointName);
  const hasBilling = Boolean(order.billingNif ?? order.billingAddress1);
  const discount = Number(order.discountAmount);

  return (
    <>
      <SEO title={`Encomenda ${order.orderNumber}`} noindex />

      <Link
        to="/admin/encomendas"
        className="inline-flex items-center gap-1.5 text-sm text-admin-text-soft hover:text-admin-primary"
      >
        <ArrowLeft aria-hidden className="h-4 w-4" /> Encomendas
      </Link>

      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1>Encomenda {order.orderNumber}</h1>
            <OrderStatusBadge status={order.status} />
          </div>
          <p className="mt-1 text-sm text-admin-text-soft">
            {formatDateTime(order.createdAt)} · {order.shippingFirstName}{' '}
            {order.shippingLastName}
          </p>
        </div>
        {order.paymentStatus === 'paid' ? (
          <a
            href={`${apiBase}/api/v1/admin/orders/${order.orderNumber}/invoice`}
            target="_blank"
            rel="noreferrer"
            className={`${actionBtn} border border-admin-border bg-white text-admin-text hover:bg-admin-surface-alt`}
          >
            <FileText aria-hidden className="h-4 w-4" /> Fatura (PDF)
          </a>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {order.status === 'paid' ? (
          <button
            type="button"
            onClick={() => setPendingAction('preparing')}
            className={`${actionBtn} bg-admin-primary text-white hover:bg-admin-primary-hover`}
          >
            <PackageCheck aria-hidden className="h-4 w-4" /> Marcar como &lsquo;A preparar&rsquo;
          </button>
        ) : null}
        {order.status === 'paid' || order.status === 'preparing' ? (
          <button
            type="button"
            onClick={() => setShipDialogOpen(true)}
            className={`${actionBtn} bg-admin-primary text-white hover:bg-admin-primary-hover`}
          >
            <Truck aria-hidden className="h-4 w-4" /> Marcar como &lsquo;Expedido&rsquo;
          </button>
        ) : null}
        {order.status === 'shipped' ? (
          <button
            type="button"
            onClick={() => setPendingAction('delivered')}
            className={`${actionBtn} bg-admin-primary text-white hover:bg-admin-primary-hover`}
          >
            <CheckCircle2 aria-hidden className="h-4 w-4" /> Marcar como &lsquo;Entregue&rsquo;
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => setShowManual((v) => !v)}
          aria-expanded={showManual}
          className={secondaryBtn}
        >
          Alterar estado manualmente
        </button>
      </div>

      {showManual ? (
        <div className="mt-3 rounded-admin-lg border border-admin-border bg-admin-surface p-4">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={manualStatus}
              onChange={(e) => setManualStatus(e.target.value as OrderStatus)}
              className={inputClass}
            >
              {ORDER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t.orderStatus[s]}
                </option>
              ))}
            </select>
            <input
              value={manualNote}
              placeholder="Nota (opcional)"
              onChange={(e) => setManualNote(e.target.value)}
              className={inputClass}
            />
            {manualStatus === 'shipped' ? (
              <>
                <select
                  value={trackingCarrier}
                  onChange={(e) => setTrackingCarrier(e.target.value as TrackingCarrier)}
                  className={inputClass}
                >
                  {TRACKING_CARRIERS.map((c) => (
                    <option key={c} value={c}>
                      {c.toUpperCase()}
                    </option>
                  ))}
                </select>
                <input
                  value={trackingNumber}
                  placeholder="Número de rastreio"
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  className={inputClass}
                />
              </>
            ) : null}
            <button
              type="button"
              onClick={() => {
                if (manualStatus === 'shipped' && trackingNumber.trim().length < 3) {
                  toast.error('Número de rastreio obrigatório.');
                  return;
                }
                setManualConfirm(true);
              }}
              disabled={manualStatus === order.status && !manualNote}
              className={primaryBtn}
            >
              Atualizar
            </button>
          </div>
          <p className="mt-2 text-xs text-admin-muted">
            Usa os botões acima para o fluxo normal. Esta opção permite qualquer alteração —
            algumas notificam a cliente por email.
          </p>
        </div>
      ) : null}

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <SectionCard title="Itens">
            <table className="w-full">
              <thead>
                <tr className="border-b border-admin-border">
                  <th className={thClass}>Produto</th>
                  <th className={thRight}>Preço unit.</th>
                  <th className={`${thClass} text-center`}>Qtd.</th>
                  <th className={thRight}>Total</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((i) => (
                  <tr key={i.id} className="border-b border-admin-border last:border-b-0">
                    <td className="p-3">
                      {i.productName}
                      {i.variantSize || i.variantColour ? (
                        <>
                          <br />
                          <small className="text-admin-muted">
                            {[i.variantSize, i.variantColour].filter(Boolean).join(' · ')}
                          </small>
                        </>
                      ) : null}
                    </td>
                    <td className="p-3 text-right text-admin-text-soft">
                      {formatEur(i.unitPrice)}
                    </td>
                    <td className="p-3 text-center">{i.quantity}</td>
                    <td className="p-3 text-right">{formatEur(i.lineTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <dl className="mt-3 ml-auto max-w-xs space-y-1 text-sm">
              <div className="flex justify-between">
                <dt className="text-admin-text-soft">Subtotal</dt>
                <dd>{formatEur(order.subtotal)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-admin-text-soft">Envio</dt>
                <dd>{formatEur(order.shippingCost)}</dd>
              </div>
              {discount > 0 ? (
                <div className="flex justify-between">
                  <dt className="text-admin-text-soft">
                    Desconto{order.couponCode ? ` (${order.couponCode})` : ''}
                  </dt>
                  <dd>−{formatEur(order.discountAmount)}</dd>
                </div>
              ) : null}
              <div className="flex justify-between border-t border-admin-border pt-1 text-base font-semibold">
                <dt>Total</dt>
                <dd>{formatEur(order.total)}</dd>
              </div>
            </dl>
          </SectionCard>

          {order.customerNotes ? (
            <SectionCard title="Notas da cliente">
              <p className="whitespace-pre-wrap text-sm text-admin-text">
                {order.customerNotes}
              </p>
            </SectionCard>
          ) : null}

          <SectionCard title="Histórico">
            {order.history.length === 0 ? (
              <p className="text-sm text-admin-text-soft">Sem histórico.</p>
            ) : (
              <ol className="space-y-4">
                {order.history.map((h) => (
                  <li key={h.id} className="flex gap-3">
                    <span
                      aria-hidden
                      className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-admin-primary"
                    />
                    <div>
                      <p className="text-sm font-medium">
                        {t.orderStatus[h.status as OrderStatus] ?? h.status}
                      </p>
                      {h.note ? (
                        <p className="text-sm text-admin-text-soft">{h.note}</p>
                      ) : null}
                      <p className="text-xs text-admin-muted">{formatDateTime(h.createdAt)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </SectionCard>
        </div>

        <div className="space-y-4">
          <SectionCard title="Cliente">
            <div className="space-y-1 text-sm">
              <p className="font-medium">
                {order.shippingFirstName} {order.shippingLastName}
                {order.guestEmail ? (
                  <span className="ml-2 rounded-full bg-admin-surface-alt px-2 py-0.5 text-xs font-normal text-admin-text-soft">
                    Convidada
                  </span>
                ) : null}
              </p>
              {order.customerEmail ? (
                <p>
                  <a
                    href={`mailto:${order.customerEmail}`}
                    className="text-admin-primary hover:underline"
                  >
                    {order.customerEmail}
                  </a>
                </p>
              ) : (
                <p className="text-admin-muted">Sem email associado</p>
              )}
            </div>
          </SectionCard>

          <SectionCard title={isPickup ? 'Recolha' : 'Entrega'}>
            <div className="space-y-1 text-sm text-admin-text-soft">
              {isPickup ? (
                <>
                  <p className="font-medium text-admin-text">{order.pickupPointName}</p>
                  {order.pickupPointAddress ? (
                    <p className="whitespace-pre-wrap">{order.pickupPointAddress}</p>
                  ) : null}
                  {order.pickupDeadlineDate ? (
                    <p>
                      Recolher até{' '}
                      <strong className="text-admin-text">
                        {formatDate(order.pickupDeadlineDate)}
                      </strong>
                    </p>
                  ) : null}
                </>
              ) : (
                <address className="not-italic">
                  {order.shippingAddress1}
                  {order.shippingAddress2 ? (
                    <>
                      <br />
                      {order.shippingAddress2}
                    </>
                  ) : null}
                  <br />
                  {order.shippingPostalCode} {order.shippingCity}
                  <br />
                  {order.shippingDistrict}, {order.shippingCountry}
                </address>
              )}
              {order.trackingCarrier && order.trackingNumber ? (
                <p className="pt-1">
                  Rastreio:{' '}
                  <a
                    href={cttTrackingUrl(order.trackingNumber)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-admin-primary hover:underline"
                  >
                    {order.trackingCarrier.toUpperCase()} {order.trackingNumber}
                    <ExternalLink aria-hidden className="h-3.5 w-3.5" />
                  </a>
                </p>
              ) : null}
            </div>
          </SectionCard>

          <SectionCard title="Pagamento">
            <div className="space-y-1 text-sm text-admin-text-soft">
              <p>
                <span className="text-admin-text">
                  {t.paymentMethod[order.paymentMethod]}
                </span>{' '}
                — {t.paymentStatus[order.paymentStatus]}
              </p>
              {order.paidAt ? <p>Pago a {formatDateTime(order.paidAt)}</p> : null}
              {order.paymentEntity && order.paymentRef ? (
                <p>
                  Entidade {order.paymentEntity} · Ref.ª {order.paymentRef}
                </p>
              ) : null}
              {order.easypayId ? (
                <p className="font-mono text-xs text-admin-muted">
                  EasyPay: {order.easypayId}
                </p>
              ) : null}
            </div>
          </SectionCard>

          {hasBilling ? (
            <SectionCard title="Faturação">
              <div className="space-y-1 text-sm text-admin-text-soft">
                {order.billingNif ? (
                  <p>
                    NIF: <span className="text-admin-text">{order.billingNif}</span>
                  </p>
                ) : null}
                {order.billingFirstName ? (
                  <p>
                    {order.billingFirstName} {order.billingLastName}
                  </p>
                ) : null}
                {order.billingAddress1 ? (
                  <address className="not-italic">
                    {order.billingAddress1}
                    <br />
                    {order.billingPostalCode} {order.billingCity}
                    <br />
                    {order.billingCountry}
                  </address>
                ) : null}
              </div>
            </SectionCard>
          ) : null}

          <SectionCard title="Notas internas">
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              rows={4}
              maxLength={5000}
              placeholder="Só a equipa vê estas notas."
              className={`${inputClass} w-full resize-y`}
            />
            <button
              type="button"
              onClick={() => void saveNotes()}
              disabled={savingNotes || notesDraft === (order.internalNotes ?? '')}
              className={`${secondaryBtn} mt-2`}
            >
              {savingNotes ? 'A guardar…' : 'Guardar notas'}
            </button>
          </SectionCard>
        </div>
      </div>

      {pendingAction ? (
        <ConfirmDialog
          title={
            pendingAction === 'preparing'
              ? "Marcar como 'A preparar'"
              : "Marcar como 'Entregue'"
          }
          message={
            pendingAction === 'preparing' ? (
              <>
                A encomenda{' '}
                <strong className="font-semibold text-admin-text">{order.orderNumber}</strong>{' '}
                vai passar a &lsquo;A preparar&rsquo; e a cliente é notificada por email.
              </>
            ) : (
              <>
                A encomenda{' '}
                <strong className="font-semibold text-admin-text">{order.orderNumber}</strong>{' '}
                vai passar a &lsquo;Entregue&rsquo; e a cliente recebe um email com o convite
                para avaliar.
              </>
            )
          }
          confirmLabel="Confirmar"
          busyLabel="A atualizar…"
          onConfirm={confirmGuidedAction}
          onClose={() => setPendingAction(null)}
        />
      ) : null}

      {shipDialogOpen ? (
        <ShipOrderDialog
          orderNumber={order.orderNumber}
          onConfirm={shipOrder}
          onClose={() => setShipDialogOpen(false)}
        />
      ) : null}

      {manualConfirm ? (
        <ConfirmDialog
          title="Alterar estado"
          message={
            <>
              Alterar o estado da encomenda{' '}
              <strong className="font-semibold text-admin-text">{order.orderNumber}</strong>{' '}
              de &lsquo;{t.orderStatus[order.status]}&rsquo; para &lsquo;
              {t.orderStatus[manualStatus]}&rsquo;? Algumas alterações notificam a cliente por
              email.
            </>
          }
          confirmLabel="Alterar estado"
          busyLabel="A atualizar…"
          onConfirm={submitManual}
          onClose={() => setManualConfirm(false)}
        />
      ) : null}
    </>
  );
}
